import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {createProtoPersister, type PersistenceResult} from '@github-ui/react-query-persister'
import {useAnalytics} from '@github-ui/use-analytics'
import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {
  FileMetadataResponseSchema,
  type FileMetadataResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_metadata'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {fileMetadataPath} from '../../routes/files-changed-routes'
import {FileMetadataError, reportFileMetadataError} from './merge-file-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {FilesRoutePayload} from '../payloads/files'

interface FileMetadataQueryArguments {
  owner: string
  repo: string
  pullRequestNumber: string
  baseOid: string
  headOid: string
}

interface FileMetadataQueryOptions extends FileMetadataQueryArguments {
  routeData: FilesRoutePayload
}

interface FileMetadataDataRouterResponse {
  payload?: {
    pullRequestsFileMetadataRoute?: unknown
  }
}

export const TANSTACK_MEMORY_HIT = 'tanstack_memory_hit' as const

export type FileMetadataCacheResult = PersistenceResult | typeof TANSTACK_MEMORY_HIT

export function fileMetadataQueryKey({owner, repo, pullRequestNumber, baseOid, headOid}: FileMetadataQueryArguments) {
  return ['prs-file-metadata', owner, repo, pullRequestNumber, `baseOid:${baseOid}`, `headOid:${headOid}`] as const
}

export function useFileMetadata(
  args: FileMetadataQueryOptions,
  onCacheResult?: (result: FileMetadataCacheResult) => void,
) {
  const {sendAnalyticsEvent} = useAnalytics()
  const queryKey = fileMetadataQueryKey(args)

  const persister = useMemo(
    () =>
      createProtoPersister({
        schema: FileMetadataResponseSchema,
        sendAnalyticsEvent,
        onPersistenceResult: onCacheResult,
        enabled: isFeatureEnabled('prs_files_changed_cache_split_routes'),
        // all data in this endpoint is immutable, so setting alwaysRefetch:false here
        alwaysRefetch: false,
        prefix: 'prs-file-metadata',
      }),
    [onCacheResult, sendAnalyticsEvent],
  )

  return useQuery({
    queryKey,
    schema: FileMetadataResponseSchema,
    queryFn: ({signal}) => fetchFileMetadata(args, signal),
    persister,
    staleTime: Infinity,
  })
}

export async function fetchFileMetadata(
  {owner, repo, pullRequestNumber, baseOid, headOid, routeData}: FileMetadataQueryOptions,
  signal: AbortSignal,
): Promise<FileMetadataResponse> {
  let metadata: FileMetadataResponse
  const routeId = 'pullRequestsFileMetadataRoute'
  const queryName = 'fileMetadata'

  try {
    const {json, isProtoJson} = await queryFnFetchWithProtoSignal<FileMetadataDataRouterResponse>({
      routeId,
      queryName,
      queryDeps: {
        pathname: fileMetadataPath({
          owner,
          repo,
          pr_number: pullRequestNumber,
          baseSha: baseOid,
          headSha: headOid,
        }),
        init: {signal},
      },
    })
    const payload = json.payload?.pullRequestsFileMetadataRoute
    metadata = payload
      ? isProtoJson
        ? fromJson({schema: FileMetadataResponseSchema, raw: payload, routeId, queryName})
        : (payload as FileMetadataResponse)
      : fileMetadataFromRouteData(routeData, routeId, queryName)
  } catch (error) {
    signal.throwIfAborted()
    const fileMetadataError = new FileMetadataError('File metadata request failed', 'request_failed', {cause: error})
    reportFileMetadataError(fileMetadataError.reason)
    return fileMetadataFromRouteData(routeData, routeId, queryName)
  }

  if (metadata.baseSha !== baseOid || metadata.headSha !== headOid) {
    const fileMetadataError = new FileMetadataError(
      'File metadata response does not match the requested comparison',
      'comparison_mismatch',
    )
    reportFileMetadataError(fileMetadataError.reason)
    return fileMetadataFromRouteData(routeData, routeId, queryName)
  }

  return metadata
}

function fileMetadataFromRouteData(
  routeData: FilesRoutePayload,
  routeId: string,
  queryName: string,
): FileMetadataResponse {
  const {baseOid, headOid} = routeData.pullRequest.comparison

  return fromJson({
    schema: FileMetadataResponseSchema,
    routeId,
    queryName,
    raw: {
      baseSha: baseOid,
      headSha: headOid,
      files: routeData.diffSummaries.map(summary => ({
        changeType: toFileMetadataChangeType(summary.changeType),
        path: summary.path,
        linesAdded: summary.linesAdded,
        linesChanged: summary.linesChanged,
        linesDeleted: summary.linesDeleted,
      })),
      commits: routeData.commits.map(commit => ({
        actorLogin: commit.actorLogin,
        ...(commit.createdAt ? {createdAt: commit.createdAt} : {}),
        messageHeadline: commit.messageHeadline,
        oid: commit.oid,
        shortOid: commit.shortOid,
      })),
      counts: {
        files: routeData.diffSummaries.length,
        linesAdded: routeData.diffSummaries.reduce((total, summary) => total + summary.linesAdded, 0),
        linesChanged: routeData.diffSummaries.reduce((total, summary) => total + summary.linesChanged, 0),
        linesDeleted: routeData.diffSummaries.reduce((total, summary) => total + summary.linesDeleted, 0),
      },
    },
  })
}

function toFileMetadataChangeType(changeType: string) {
  switch (changeType) {
    case 'ADDED':
    case 'CHANGED':
    case 'COPIED':
    case 'DELETED':
    case 'EMPTY':
    case 'MODIFIED':
    case 'REMOVED':
    case 'RENAMED':
    case 'UNCHANGED':
      return `CHANGE_TYPE_${changeType}`
    default:
      return 'CHANGE_TYPE_UNSPECIFIED'
  }
}
